import { CommonErrorCodes, ErrorDomains, ErrorCategory, Domains } from '@explorins/pers-shared';

/**
 * JWT token expiration utilities
 * Native implementation without external dependencies for optimal performance
 */
/**
 * Decode JWT payload without verification
 * @param token JWT token (with or without Bearer prefix)
 * @returns Decoded payload or null if invalid
 */
const decodeJwtPayload = (token) => {
    const cleanToken = token.replace(/^Bearer\s+/i, '');
    if (!cleanToken || cleanToken.split('.').length !== 3) {
        return null;
    }
    try {
        return JSON.parse(atob(cleanToken.split('.')[1]));
    }
    catch {
        return null;
    }
};
/**
 * Check if JWT token is expired or expiring within margin
 * @param token JWT token (with or without Bearer prefix)
 * @param margin Safety margin in seconds before expiry (default: 60)
 */
const isTokenExpired = (token, margin = 60) => {
    const payload = decodeJwtPayload(token);
    if (!payload)
        return true;
    if (!payload.exp)
        return false;
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < (currentTime + margin);
};
/**
 * Get remaining time-to-live for a JWT token in seconds
 * @param token JWT token (with or without Bearer prefix)
 * @returns Remaining TTL in seconds, or null if invalid/expired
 */
const getTokenTimeToLive = (token) => {
    const payload = decodeJwtPayload(token);
    if (!payload?.exp)
        return null;
    const currentTime = Math.floor(Date.now() / 1000);
    const ttl = payload.exp - currentTime;
    return ttl > 0 ? ttl : null;
};

/**
 * PERS SDK Error Handling - Optimized for Performance
 *
 * Consolidated API and auth errors for fast SDK performance
 * Uses @explorins/pers-shared when available, fallback to SDK errors
 */
/**
 * SDK-internal error codes for auth flow control
 * These are not backend errors - they're used for SDK-internal state management
 */
const SdkErrorCodes = {
    TOKEN_REFRESH_NEEDED: 'TOKEN_REFRESH_NEEDED',
    PROVIDER_TOKEN_REFRESH_NEEDED: 'PROVIDER_TOKEN_REFRESH_NEEDED',
    LOGOUT_REQUIRED: 'LOGOUT_REQUIRED',
    NETWORK_ERROR: 'NETWORK_ERROR',
    API_ERROR: 'API_ERROR',
};
// Fast type guards and utilities
class ErrorUtils {
    /**
     * Fast token expiration detection
     */
    static isTokenExpiredError(error) {
        if (typeof error !== 'object' || error === null)
            return false;
        const err = error;
        // Check for explicit token expired indicators
        if (err?.code === CommonErrorCodes.TOKEN_EXPIRED || err?.errorCode === CommonErrorCodes.TOKEN_EXPIRED) {
            return true;
        }
        // Check in response data
        const apiError = err?.response?.data || err;
        if (apiError?.code === CommonErrorCodes.TOKEN_EXPIRED || apiError?.errorCode === CommonErrorCodes.TOKEN_EXPIRED) {
            return true;
        }
        // Any 401 error is treated as token expiration
        const status = err?.status || err?.statusCode || err?.response?.status;
        if (status === 401) {
            return true;
        }
        // Check for token-related messages
        const message = apiError?.message || err?.message || '';
        if (message && typeof message === 'string') {
            const lowerMessage = message.toLowerCase();
            return lowerMessage.includes('token') ||
                lowerMessage.includes('unauthorized') ||
                lowerMessage.includes('authentication');
        }
        return false;
    }
    /**
     * Fast error message extraction
     * Platform-agnostic: handles Axios (response.data), Angular (error), RN (message parsing)
     */
    static getMessage(error) {
        if (typeof error !== 'object' || error === null)
            return 'Unknown error';
        const err = error;
        // Try structured error first (check multiple formats: axios, Angular, etc.)
        const apiError = err?.response?.data || err?.error || err;
        return apiError?.message || apiError?.detail || err?.message || 'Request failed';
    }
    /**
     * Fast status code extraction
     */
    static getStatus(error) {
        if (typeof error !== 'object' || error === null)
            return null;
        const err = error;
        // Direct properties first
        return err?.status || err?.statusCode || err?.response?.status || null;
    }
    /**
     * Fast retryability check
     */
    static isRetryable(error) {
        if (typeof error !== 'object' || error === null)
            return false;
        const err = error;
        // Check explicit retryable property
        const apiError = err?.response?.data || err;
        if (typeof apiError?.retryable === 'boolean')
            return apiError.retryable;
        if (typeof err?.retryable === 'boolean')
            return err.retryable;
        // Status-based check
        const status = this.getStatus(error);
        return status === null || status >= 500 || status === 429;
    }
    /**
     * Extract detailed error information from backend response
     * Tries multiple extraction strategies to find structured error data
     */
    static extractBackendErrorDetails(error) {
        try {
            if (!error || typeof error !== 'object') {
                return this.createFallbackError(error);
            }
            const httpError = error;
            // Try extraction strategies in priority order
            const candidates = [
                this.parseHttpClientError(error), // React Native format
                httpError.response && typeof httpError.response === 'object'
                    ? httpError.response.data
                    : undefined, // Axios/fetch format
                httpError.error, // Angular HttpErrorResponse
                httpError // Direct error object
            ];
            for (const candidate of candidates) {
                if (this.hasStructuredErrorFields(candidate)) {
                    return candidate;
                }
            }
            return this.createFallbackError(error);
        }
        catch {
            return this.createFallbackError(error);
        }
    }
    /**
     * Check if object has structured error fields from backend (StructuredError)
     */
    static hasStructuredErrorFields(obj) {
        return obj !== null && typeof obj === 'object' &&
            (('code' in obj && 'category' in obj) ||
                ('status' in obj && ('detail' in obj || 'message' in obj || 'title' in obj)));
    }
    /**
     * Create fallback error structure when no structured error found
     */
    static createFallbackError(error) {
        return {
            status: this.getStatus(error) || 500,
            title: 'Request failed',
            message: this.getMessage(error),
            detail: this.getMessage(error),
            code: CommonErrorCodes.TECHNICAL_ERROR,
            category: ErrorCategory.TECHNICAL,
            domain: ErrorDomains.EXTERNAL,
            retryable: this.isRetryable(error)
        };
    }
    /**
     * Parse React Native HTTP client error format: "HTTP 401: Unauthorized - {json}"
     */
    static parseHttpClientError(error) {
        try {
            const message = error?.message || '';
            if (typeof message !== 'string' || !message.includes(' - ')) {
                return null;
            }
            const jsonStart = message.lastIndexOf(' - ');
            const jsonStr = message.substring(jsonStart + 3).trim();
            if (!jsonStr)
                return null;
            try {
                return JSON.parse(jsonStr);
            }
            catch {
                return null;
            }
        }
        catch {
            return null;
        }
    }
    /**
     * Check if error is from PERS API (uses @explorins/pers-shared format)
     */
    static isPersApiError(error) {
        return typeof error === 'object' && error !== null &&
            'code' in error && 'domain' in error && 'category' in error;
    }
}
// SDK-specific error classes for auth flows
class TokenRefreshNeeded extends Error {
    constructor(refreshToken) {
        super('Token refresh needed');
        this.refreshToken = refreshToken;
        this.errorCode = SdkErrorCodes.TOKEN_REFRESH_NEEDED;
        this.domain = Domains.AUTHENTICATION;
        this.category = ErrorCategory.SECURITY;
        this.retryable = true;
        this.name = 'TokenRefreshNeeded';
    }
}
class ProviderTokenRefreshNeeded extends Error {
    constructor(providerToken) {
        super('Provider token refresh needed');
        this.providerToken = providerToken;
        this.errorCode = SdkErrorCodes.PROVIDER_TOKEN_REFRESH_NEEDED;
        this.domain = Domains.AUTHENTICATION;
        this.category = ErrorCategory.SECURITY;
        this.retryable = true;
        this.name = 'ProviderTokenRefreshNeeded';
    }
}
class LogoutRequired extends Error {
    constructor(message) {
        super(message);
        this.errorCode = SdkErrorCodes.LOGOUT_REQUIRED;
        this.domain = Domains.AUTHENTICATION;
        this.category = ErrorCategory.SECURITY;
        this.retryable = false;
        this.name = 'LogoutRequired';
    }
}
/**
 * API error with backend StructuredError details
 */
class PersApiError extends Error {
    constructor(message, endpoint, method, statusCode, backendError) {
        super(backendError?.message || message);
        this.endpoint = endpoint;
        this.method = method;
        this.name = 'PersApiError';
        // Use backend error details directly
        this.code = backendError?.code || CommonErrorCodes.TECHNICAL_ERROR;
        this.domain = backendError?.domain || ErrorDomains.EXTERNAL;
        this.category = backendError?.category || ErrorCategory.TECHNICAL;
        this.retryable = backendError?.retryable ?? (statusCode ? statusCode >= 500 : false);
        this.message = backendError?.message || message;
        this.status = backendError?.status || statusCode || 500;
        this.title = backendError?.title || 'Request failed';
        this.detail = backendError?.detail || backendError?.message || message;
        this.details = backendError?.details;
    }
}
class AuthenticationError extends PersApiError {
    constructor(message, endpoint, method, backendError) {
        super(message, endpoint, method, 401, {
            ...backendError,
            domain: Domains.AUTHENTICATION,
            category: ErrorCategory.SECURITY
        });
        this.name = 'AuthenticationError';
    }
}
class NetworkError extends Error {
    constructor(message) {
        super(message);
        this.code = CommonErrorCodes.EXTERNAL_SERVICE_ERROR;
        this.domain = ErrorDomains.EXTERNAL;
        this.category = ErrorCategory.INFRASTRUCTURE;
        this.retryable = true;
        this.status = 0;
        this.title = 'Network error';
        this.name = 'NetworkError';
        this.message = message;
        this.detail = message;
    }
}
const ApiErrorDetector = {
    getErrorMessage: (error) => ErrorUtils.getMessage(error),
    getStatusCode: (error) => ErrorUtils.getStatus(error),
    isRetryable: (error) => ErrorUtils.isRetryable(error),
    isAuthError: (error) => error instanceof TokenRefreshNeeded ||
        error instanceof ProviderTokenRefreshNeeded ||
        error instanceof LogoutRequired ||
        ErrorUtils.isTokenExpiredError(error)
};

/**
 * Centralized Cache Service for PERS SDK
 * Simple, efficient caching with memory management
 */
const DEFAULT_CACHE_CONFIG = {
    defaultTtl: 30 * 60 * 1000, // 30 minutes
    maxMemoryMB: 50, // 50MB cache limit
    cleanupInterval: 5 * 60 * 1000, // Cleanup every 5 minutes
    maxEntries: 10000, // Entry count limit
    evictionBatchPercent: 0.2, // Remove 20% when evicting
};
// Constants for memory estimation
const ENTRY_OVERHEAD_BYTES = 64;
const DEFAULT_OBJECT_SIZE_BYTES = 1024;
const MEMORY_CHECK_INTERVAL_MS = 60000; // Lazy check every 60s
class CacheService {
    constructor(config) {
        this.cache = new Map();
        this.cleanupTimer = null;
        this.lastMemoryCheck = 0;
        this.pendingFetches = new Map();
        this.accessOrder = new Set();
        this.stats = {
            hits: 0,
            misses: 0,
            errors: 0
        };
        this.config = { ...DEFAULT_CACHE_CONFIG, ...config };
        this.startCleanupTimer();
    }
    /**
     * Set a value in cache with optional TTL
     */
    set(key, value, ttl) {
        if (!key || value === undefined)
            return;
        const entry = {
            value,
            timestamp: Date.now(),
            ttl: ttl || this.config.defaultTtl,
            lastAccessed: Date.now()
        };
        this.cache.set(key, entry);
        this.accessOrder.add(key);
        // Fast entry count check first
        if (this.cache.size > this.config.maxEntries) {
            this.evictOldestEntries();
        }
        // Lazy memory check (only every 10s)
        const now = Date.now();
        if (now - this.lastMemoryCheck > MEMORY_CHECK_INTERVAL_MS) {
            this.lastMemoryCheck = now;
            this.enforceMemoryLimit();
        }
    }
    /**
     * Get a value from cache
     */
    get(key) {
        const entry = this.cache.get(key);
        if (!entry) {
            this.stats.misses++;
            return null;
        }
        // Check if expired
        const now = Date.now();
        if (now - entry.timestamp > entry.ttl) {
            this.cache.delete(key);
            this.accessOrder.delete(key);
            this.stats.misses++;
            return null;
        }
        // Update access order efficiently
        this.accessOrder.delete(key);
        this.accessOrder.add(key);
        entry.lastAccessed = now;
        this.stats.hits++;
        return entry.value;
    }
    /**
     * Get or set pattern - common caching pattern with race condition protection
     */
    async getOrSet(key, fetcher, ttl) {
        const cached = this.get(key);
        if (cached !== null)
            return cached;
        // Prevent duplicate fetches
        if (this.pendingFetches.has(key)) {
            return this.pendingFetches.get(key);
        }
        const fetchPromise = fetcher()
            .then(value => {
            this.pendingFetches.delete(key);
            if (value !== undefined) {
                this.set(key, value, ttl);
            }
            return value;
        })
            .catch(error => {
            this.pendingFetches.delete(key);
            this.stats.errors++;
            throw error;
        });
        this.pendingFetches.set(key, fetchPromise);
        return fetchPromise;
    }
    /**
     * Delete a specific key
     */
    delete(key) {
        this.accessOrder.delete(key);
        return this.cache.delete(key);
    }
    /**
     * Clear all keys matching a prefix
     */
    clearByPrefix(prefix) {
        const keysToDelete = Array.from(this.cache.keys()).filter(key => key.startsWith(prefix));
        keysToDelete.forEach(key => {
            this.cache.delete(key);
            this.accessOrder.delete(key);
        });
        return keysToDelete.length;
    }
    /**
     * Clear all cache
     */
    clear() {
        this.cache.clear();
        this.accessOrder.clear();
        this.pendingFetches.clear();
        this.stats = { hits: 0, misses: 0, errors: 0 };
    }
    /**
     * Force cleanup of expired entries
     */
    cleanup() {
        const now = Date.now();
        const keysToDelete = [];
        for (const [key, entry] of this.cache.entries()) {
            if (now - entry.timestamp > entry.ttl) {
                keysToDelete.push(key);
            }
        }
        keysToDelete.forEach(key => {
            this.cache.delete(key);
            this.accessOrder.delete(key);
        });
        return keysToDelete.length;
    }
    /**
     * Stop cleanup timer and clear cache
     */
    destroy() {
        this.stopCleanupTimer();
        this.clear();
    }
    /**
     * Create a namespaced cache interface
     */
    createNamespace(namespace) {
        if (!namespace || namespace.includes(':')) {
            throw new Error('Namespace must be non-empty and cannot contain ":"');
        }
        return {
            set: (key, value, ttl) => this.set(`${namespace}:${key}`, value, ttl),
            get: (key) => this.get(`${namespace}:${key}`),
            getOrSet: (key, fetcher, ttl) => this.getOrSet(`${namespace}:${key}`, fetcher, ttl),
            delete: (key) => this.delete(`${namespace}:${key}`),
            clear: () => this.clearByPrefix(`${namespace}:`)
        };
    }
    startCleanupTimer() {
        this.stopCleanupTimer();
        this.cleanupTimer = setInterval(() => {
            this.safeCleanup();
        }, this.config.cleanupInterval);
        // Platform-agnostic: Prevent hanging process in Node.js environments
        if (this.cleanupTimer && typeof this.cleanupTimer.unref === 'function') {
            this.cleanupTimer.unref();
        }
    }
    stopCleanupTimer() {
        if (this.cleanupTimer) {
            clearInterval(this.cleanupTimer);
            this.cleanupTimer = null;
        }
    }
    safeCleanup() {
        try {
            this.cleanup();
            this.enforceMemoryLimit();
        }
        catch (error) {
            this.stats.errors++;
            // Platform-agnostic error logging
            if (typeof console !== 'undefined' && console.warn) {
                console.warn('Cache cleanup error:', error);
            }
        }
    }
    estimateValueSize(value) {
        if (typeof value === 'string') {
            return value.length * 2; // UTF-16 encoding
        }
        if (typeof value === 'number' || typeof value === 'boolean') {
            return 8; // Primitive values
        }
        if (value === null || value === undefined) {
            return 4;
        }
        try {
            // More accurate JSON-based estimation for serializable objects
            const jsonString = JSON.stringify(value);
            return jsonString.length * 2;
        }
        catch {
            // Fallback for non-serializable objects
            if (Array.isArray(value)) {
                return value.length * 100; // Better estimate for arrays
            }
            if (value && typeof value === 'object') {
                return Object.keys(value).length * 50; // Better estimate for objects
            }
            return DEFAULT_OBJECT_SIZE_BYTES;
        }
    }
    estimateMemoryUsage() {
        let totalSize = 0;
        for (const [key, entry] of this.cache.entries()) {
            totalSize += key.length * 2; // Key size
            totalSize += this.estimateValueSize(entry.value);
            totalSize += ENTRY_OVERHEAD_BYTES;
        }
        return totalSize / (1024 * 1024);
    }
    enforceMemoryLimit() {
        const memoryUsage = this.estimateMemoryUsage();
        if (memoryUsage <= this.config.maxMemoryMB || this.cache.size === 0)
            return;
        this.evictOldestEntries();
    }
    evictOldestEntries() {
        if (this.cache.size === 0)
            return;
        const batchSize = Math.floor(this.cache.size * this.config.evictionBatchPercent);
        const toRemove = Math.max(batchSize, 1);
        // Efficiently remove oldest entries using access order
        const iterator = this.accessOrder.values();
        for (let i = 0; i < toRemove; i++) {
            const result = iterator.next();
            if (result.done)
                break;
            const key = result.value;
            this.cache.delete(key);
            this.accessOrder.delete(key);
        }
    }
}
// Singleton instance
const globalCacheService = new CacheService();

/**
 * Cache module exports
 */
// Predefined cache TTL constants
const CacheTTL = {
    SHORT: 5 * 60 * 1000, // 5 minutes
    MEDIUM: 30 * 60 * 1000, // 30 minutes  
    LONG: 24 * 60 * 60 * 1000, // 24 hours
    METADATA: 24 * 60 * 60 * 1000, // 24 hours - IPFS metadata is immutable
    GATEWAY: 30 * 60 * 1000, // 30 minutes - Gateway config can change
    PROVIDER: 60 * 60 * 1000, // 1 hour - Provider connections with auth
};

export { AuthenticationError as A, CacheTTL as C, ErrorUtils as E, LogoutRequired as L, NetworkError as N, ProviderTokenRefreshNeeded as P, SdkErrorCodes as S, TokenRefreshNeeded as T, CacheService as a, PersApiError as b, ApiErrorDetector as c, decodeJwtPayload as d, getTokenTimeToLive as e, globalCacheService as g, isTokenExpired as i };
//# sourceMappingURL=index--OssIds0.js.map
